/**
 * CSV import / export of Price limits — the DS native template and the
 * RateGain / Carwiz partner export, auto-detected (CU-869fkbjdh § 3.8).
 *
 * The parsers and `toCsv` moved here from the tab **without a change of
 * logic** so they run under `node --test`; `downloadCsv` stays with the
 * dialog because it touches the DOM. `axes` is the tenant's axis
 * catalogue (`useAxisCatalog()`): a CSV names brands and products in
 * words and the model stores Price Seeker ids, and a word that resolves
 * to nothing becomes "any" plus a warning — never a silent guess.
 */
import { uid } from '../@mocks/index.js'
import { resolveFranchise, resolveInsurance, resolvePrepaid } from './axesResolve.js'

export const DS_CSV_HEADERS = [
	'franchise', 'tariff_type', 'insurance_type',
	'office_group', 'office', 'macros', 'sipps',
	'duration_unit', 'duration_min', 'duration_max',
	'date_from', 'date_to', 'min_price', 'max_price',
	// CU-869fe8jrk. Without these two, Export → Import "Replace all"
	// would turn every protection into an ordinary limit in silence.
	'protects_dates', 'name',
]
export const DS_CSV_TEMPLATE_ROWS = [
	['', '', '', '', '', '', '', '', '', '', '', '', '18', '', '', ''],
	['', '', '', 'airports', '', 'mini', '', 'days', '1', '3', '', '', '22', '', '', ''],
	['canarias', 'prepaid', 'standard', '', 'LPA-AP', '', 'CDMR', 'weeks', '1', '2', '2026-12-20', '2027-01-05', '45', '', '', ''],
	['', '', '', '', 'TFS-AP', '', 'FVAR', '', '', '', '', '', '55', '200', '', ''],
	['', '', '', '', '', '', '', '', '', '', '2026-12-26', '2026-12-31', '50', '', 'yes', 'Christmas'],
]
// RateGain / Carwiz format (real partner export).
export const RATEGAIN_HEADERS = [
	'Brand', 'PickupDateStart', 'PickupDateEnd',
	'PickupLocation', 'DropOffLocation',
	'ProductCode', 'CarCode', 'LOR', 'Min', 'Max', 'Acceptance',
]

export function toCsv(limits) {
	const rows = [DS_CSV_HEADERS.join(',')]
	for (const l of limits) {
		const macros = (l.scope_categories || [])
			.filter(c => c.type === 'macro').map(c => c.code).join('|')
		const sipps  = (l.scope_categories || [])
			.filter(c => c.type === 'sipp').map(c => c.code).join('|')
		rows.push([
			// Ids, not names: an export that round-trips through the
			// importer has to carry something the importer can resolve
			// without guessing, and a brand can be renamed.
			csvCell(l.scope_ps_provider_id),
			csvCell(l.scope_is_prepaid == null ? '' : (l.scope_is_prepaid ? 'prepaid' : 'postpaid')),
			csvCell(l.scope_ps_insurance_id),
			csvCell(l.scope_office_group),
			csvCell((l.scope_offices || []).join('|')),
			csvCell(macros),
			csvCell(sipps),
			csvCell(l.scope_duration ? 'days' : ''),
			csvCell(l.scope_duration?.min),
			csvCell(l.scope_duration?.max),
			csvCell(l.date_from),
			csvCell(l.date_to),
			csvCell(l.min_price),
			csvCell(l.max_price),
			csvCell(l.protects_dates ? 'yes' : ''),
			csvCell(l.protects_dates ? l.name : ''),
		].join(','))
	}
	return rows.join('\n')
}

export function csvCell(v) {
	if (v == null || v === '') return ''
	const s = String(v)
	return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}


export function detectCsvFormat(text) {
	const first = (text.split(/\r?\n/, 1)[0] || '').toLowerCase()
	const isRg = first.includes('brand') && first.includes('pickupdatestart')
	          && first.includes('carcode') && first.includes('lor')
	return isRg ? 'rategain' : 'ds'
}

// `axes` is threaded all the way down because a CSV names brands and
// products in words and the model stores Price Seeker ids. A word that
// resolves to nothing becomes "any" plus a warning: a price limit
// scoped to the WRONG brand is worse than one scoped to all of them.
export function parseCsv(text, axes) {
	const fmt = detectCsvFormat(text)
	const parsed = fmt === 'rategain' ? parseRateGain(text, axes) : parseDs(text, axes)
	return { format: fmt, ...parsed }
}

export function parseDs(text, axes) {
	const unresolved = new Map()
	// Records a value that could not be resolved and returns the
	// resolution anyway, so an unknown brand widens the scope instead of
	// aborting the whole import.
	const keep = (field, raw, resolved) => {
		if (resolved == null && String(raw || '').trim()) {
			unresolved.set(`${field}=${raw}`, field)
		}
		return resolved
	}
	const lines = text.split(/\r?\n/).filter(l => l.trim().length)
	if (!lines.length) return { rows: [], errors: ['File is empty.'], warnings: [] }
	const headers = splitCsvLine(lines[0])
	// Everything is optional except min_price + max_price (checked per-row).
	const rows = []
	const errors = []
	for (let i = 1; i < lines.length; i++) {
		const cells = splitCsvLine(lines[i])
		const rec = Object.fromEntries(headers.map((h, idx) => [h, cells[idx] ?? '']))
		const macros = rec.macros ? rec.macros.split(/[|;]/).filter(Boolean) : []
		const sipps  = rec.sipps  ? rec.sipps .split(/[|;]/).filter(Boolean) : []
		const scope_categories = [
			...macros.map(c => ({ type: 'macro', code: c.trim() })),
			...sipps .map(c => ({ type: 'sipp',  code: c.trim() })),
		]
		// `duration_unit` stays in the header so files exported before
		// 2026-09-30 still import, but whatever it says is ignored:
		// the engine never read it and a file claiming weeks was being
		// matched in days anyway. A range with no unit is a range in days.
		const hasDuration = rec.duration_min !== '' || rec.duration_max !== ''
		const scope_duration = hasDuration
			? { unit: 'days',
			    min: rec.duration_min === '' ? null : Number(rec.duration_min),
			    max: rec.duration_max === '' ? null : Number(rec.duration_max) }
			: null
		const minP = rec.min_price === '' ? null : Number(rec.min_price)
		const maxP = rec.max_price === '' ? null : Number(rec.max_price)
		if (minP == null && maxP == null) {
			errors.push(`Row ${i + 1}: need at least min_price or max_price.`)
			continue
		}
		rows.push({
			id: uid(),
			scope_ps_provider_id:  keep('franchise', rec.franchise,
			                            resolveFranchise(axes, rec.franchise)),
			scope_is_prepaid:      keep('tariff_type', rec.tariff_type,
			                            resolvePrepaid(rec.tariff_type)),
			scope_ps_insurance_id: keep('insurance_type', rec.insurance_type,
			                            resolveInsurance(axes, rec.insurance_type)),
			scope_office_group: rec.office_group || null,
			scope_offices:      rec.office ? rec.office.split('|').map(s => s.trim()).filter(Boolean) : null,
			scope_categories:   scope_categories.length ? scope_categories : null,
			scope_duration,
			date_from: rec.date_from || null,
			date_to:   rec.date_to   || null,
			protects_dates: /^(yes|true|1)$/i.test((rec.protects_dates || '').trim()),
			name: (rec.name || '').trim() || null,
			min_price: minP,
			max_price: maxP,
		})
	}
	const warnings = []
	if (unresolved.size) {
		warnings.push(
			`Could not match ${[...unresolved.keys()].join(', ')} to this tenant's ` +
			`catalogue — those rules were imported with that scope left as "any". ` +
			`Set them by hand, or export from this screen to get the ids.`)
	}
	return { rows, errors, warnings }
}

/**
 * Parse a RateGain / Carwiz export.
 *
 *   Brand         → scope_ps_provider_id (matched by name against the
 *                   tenant's own brands; no match = any, with a warning)
 *   ProductCode   → scope_is_prepaid (read as a word: prepaid/postpaid)
 *   (insurance)   → not present in RateGain export → left null + warning
 *   PickupLocation→ scope_offices (one code per source row)
 *   CarCode       → scope_categories: [{type:'sipp', code}]
 *   LOR           → duration range (breakpoint expansion — see below)
 *   Min / Max     → min_price / max_price
 *   Acceptance    → dropped (not in our model)
 *
 * LOR breakpoints: RateGain lists discrete anchors (1, 3, 5, 7, 10, 14,
 * 21, 28, 31…) each with its own Min/Max. In our model that becomes a
 * duration range. We expand each breakpoint to cover the interval up to
 * the NEXT breakpoint minus one day, in the context of the SAME
 * (Brand, ProductCode, PickupLocation, DropOffLocation, CarCode,
 * PickupDateStart, PickupDateEnd) group. The last breakpoint of a group
 * becomes open-ended (max: null).
 */
export function parseRateGain(text, axes) {
	const lines = text.split(/\r?\n/).filter(l => l.trim().length)
	if (!lines.length) return { rows: [], errors: ['File is empty.'], warnings: [] }
	const headers = splitCsvLine(lines[0])
	const missing = RATEGAIN_HEADERS.filter(h => !headers.includes(h))
	if (missing.length) {
		return { rows: [], errors: [`Missing RateGain columns: ${missing.join(', ')}`], warnings: [] }
	}

	const raw = []
	const errors = []
	const warnings = []
	const unresolvedRg = new Map()
	const keepRg = (field, rawValue, resolved) => {
		if (resolved == null && String(rawValue || '').trim()) {
			unresolvedRg.set(`${field}=${rawValue}`, field)
		}
		return resolved
	}
	let differentDropoff = 0
	const droppedFields = new Set()

	for (let i = 1; i < lines.length; i++) {
		const cells = splitCsvLine(lines[i])
		const rec = Object.fromEntries(headers.map((h, idx) => [h, cells[idx] ?? '']))
		const df = parseDdmmyyyy(rec.PickupDateStart)
		const dt = parseDdmmyyyy(rec.PickupDateEnd)
		if (rec.PickupDateStart && !df) errors.push(`Row ${i + 1}: bad PickupDateStart "${rec.PickupDateStart}".`)
		if (rec.PickupDateEnd   && !dt) errors.push(`Row ${i + 1}: bad PickupDateEnd "${rec.PickupDateEnd}".`)
		const lor = Number(rec.LOR)
		if (!Number.isFinite(lor) || lor <= 0) {
			errors.push(`Row ${i + 1}: LOR "${rec.LOR}" is not a positive number.`)
			continue
		}
		const minP = rec.Min === '' ? null : Number(rec.Min)
		const maxP = rec.Max === '' ? null : Number(rec.Max)
		if (minP == null && maxP == null) {
			errors.push(`Row ${i + 1}: need at least Min or Max.`)
			continue
		}
		if (rec.PickupLocation && rec.DropOffLocation
		    && rec.PickupLocation !== rec.DropOffLocation) {
			differentDropoff++
		}
		if (rec.Acceptance && rec.Acceptance !== '100') droppedFields.add('Acceptance')

		raw.push({
			franchise:  rec.Brand,
			tariff:     rec.ProductCode,
			office:     rec.PickupLocation,
			sipp:       rec.CarCode,
			date_from:  df,
			date_to:    dt,
			lor,
			min: minP,
			max: maxP,
		})
	}

	// Group by (franchise, tariff, office, sipp, date_from, date_to) so
	// LOR breakpoints inside the same group become a contiguous range set.
	const groups = new Map()
	for (const r of raw) {
		const k = [r.franchise, r.tariff, r.office, r.sipp, r.date_from, r.date_to].join('|')
		if (!groups.has(k)) groups.set(k, [])
		groups.get(k).push(r)
	}

	const rows = []
	for (const list of groups.values()) {
		list.sort((a, b) => a.lor - b.lor)
		for (let i = 0; i < list.length; i++) {
			const cur  = list[i]
			const next = list[i + 1]
			const min  = cur.lor
			const max  = next ? next.lor - 1 : null   // open-ended for the tail
			rows.push({
				id: uid(),
				scope_ps_provider_id:  keepRg('Brand', cur.franchise,
				                              resolveFranchise(axes, cur.franchise)),
				scope_is_prepaid:      keepRg('ProductCode', cur.tariff,
				                              resolvePrepaid(cur.tariff)),
				scope_ps_insurance_id: null,
				scope_office_group: null,
				scope_offices:      cur.office ? [cur.office] : null,
				scope_categories:   cur.sipp ? [{ type: 'sipp', code: cur.sipp }] : null,
				scope_duration: { unit: 'days', min, max },
				date_from: cur.date_from || null,
				date_to:   cur.date_to   || null,
				min_price: cur.min,
				max_price: cur.max,
			})
		}
	}

	if (unresolvedRg.size) {
		warnings.push(
			`Could not match ${[...unresolvedRg.keys()].join(', ')} to this tenant's ` +
			`catalogue — those rules were imported with that scope left as "any".`)
	}
	warnings.push(`Insurance type is not part of the RateGain export — every imported rule has an empty insurance scope (i.e. applies to any). Add it by hand if you need per-insurance limits.`)
	if (differentDropoff > 0) {
		warnings.push(`${differentDropoff} row${differentDropoff === 1 ? '' : 's'} had a different DropOffLocation — kept only PickupLocation (one-office model).`)
	}
	if (droppedFields.size > 0) {
		warnings.push(`Ignored fields: ${[...droppedFields].join(', ')} — not part of our model yet.`)
	}
	warnings.push(`LOR breakpoints expanded into contiguous day ranges (LOR=N covers N up to the next breakpoint − 1).`)

	return { rows, errors, warnings }
}

export function parseDdmmyyyy(s) {
	if (!s) return null
	const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(s.trim())
	if (!m) return null
	return `${m[3]}-${m[2]}-${m[1]}`
}

export function splitCsvLine(line) {
	const out = []
	let cur = ''
	let inQ = false
	for (let i = 0; i < line.length; i++) {
		const c = line[i]
		if (inQ) {
			if (c === '"' && line[i + 1] === '"') { cur += '"'; i++ }
			else if (c === '"') inQ = false
			else cur += c
		} else {
			if (c === ',') { out.push(cur); cur = '' }
			else if (c === '"' && !cur) inQ = true
			else cur += c
		}
	}
	out.push(cur)
	return out.map(s => s.trim())
}
